import { Check, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/layout/MobileShell";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { resolveScreenBand } from "@/lib/format";
import { DEFAULT_WEEK_START_DAY, isLastDayOfWeek } from "@/lib/week";
import { familyService } from "@/services/family.service";
import { jobsService } from "@/services/jobs.service";
import { weekLogService } from "@/services/weekLog.service";

export function ChildConfirmWeek() {
  const { profile } = useProfile();
  const weekLogState = useObservable(weekLogService.state$, {
    week: null,
    logs: [],
    siblingTotals: {},
    isPreviousWeek: false,
    pendingPreviousWeek: null,
    allWeeks: [],
    allLogs: [],
    loading: false,
  });
  const jobs = useObservable(jobsService.jobs$, []);
  const [confirming, setConfirming] = useState(false);
  const [screenHoursMap, setScreenHoursMap] = useState<
    Record<string, string | null>
  >({});
  const [screenErrorSet, setScreenErrorSet] = useState<Record<string, boolean>>(
    {},
  );
  const navigate = useNavigate();

  const week = weekLogState.week;
  const weekStatus = week?.data.status;
  const offset =
    familyService.snapshot.settings?.week_start_day ?? DEFAULT_WEEK_START_DAY;
  const canConfirmNow =
    weekStatus === "open" &&
    (weekLogState.isPreviousWeek || isLastDayOfWeek(offset));

  useEffect(() => {
    if (week && (weekStatus !== "open" || !canConfirmNow)) {
      navigate("/child/week", { replace: true });
    }
  }, [week, weekStatus, canConfirmNow, navigate]);

  if (!profile) return null;
  if (week && (weekStatus !== "open" || !canConfirmNow)) return null;
  const { logs } = weekLogState;
  const weekTotal = weekLogService.weekTotal();

  const screenJobs = jobs.filter(
    (j) => j.data.kind === "screen" && j.data.bands?.length,
  );

  const screenJobStates = screenJobs.map((job) => {
    const logged = logs.some((l) => l.data.job_id === job.id);
    const hours = screenHoursMap[job.id] ?? null;
    const normalized = (hours ?? "").replace(",", ".");
    const num = parseFloat(normalized);
    const maxHours = job.data.bands?.at(-1)?.max ?? null;
    const valid =
      hours !== null &&
      hours !== "" &&
      !Number.isNaN(num) &&
      num >= 0 &&
      (maxHours === null || num <= maxHours);
    const band =
      job.data.bands && valid ? resolveScreenBand(job.data.bands, num) : null;
    return {
      job,
      logged,
      hours,
      num,
      maxHours,
      valid,
      band,
      needsLogging: !logged,
    };
  });

  const canConfirm = screenJobStates
    .filter((s) => s.needsLogging)
    .every((s) => s.valid);

  const pendingScreenTotal = screenJobStates
    .filter((s) => s.needsLogging && s.valid)
    .reduce((sum, s) => sum + (s.band?.amount ?? 0), 0);
  const displayTotal = weekTotal + pendingScreenTotal;

  // Aggregate logs by job (exclude screen — shown separately)
  const summary = Object.values(
    logs
      .filter((l) => l.data.job_snapshot.kind !== "screen")
      .reduce<
        Record<
          string,
          { emoji: string; title: string; count: number; total: number }
        >
      >((acc, l) => {
        const { job_id, job_snapshot, amount } = l.data;
        if (!acc[job_id]) {
          acc[job_id] = {
            emoji: job_snapshot.emoji,
            title: job_snapshot.title,
            count: 0,
            total: 0,
          };
        }
        acc[job_id].count += 1;
        acc[job_id].total += amount;
        return acc;
      }, {}),
  );

  async function handleConfirm() {
    if (confirming) return;
    if (!canConfirm) {
      const errors: Record<string, boolean> = {};
      for (const s of screenJobStates) {
        if (s.needsLogging && !s.valid) errors[s.job.id] = true;
      }
      setScreenErrorSet(errors);
      return;
    }
    setScreenErrorSet({});
    setConfirming(true);
    try {
      for (const s of screenJobStates) {
        if (s.needsLogging) {
          await weekLogService.logScreenTime(s.job, s.num);
        }
      }
      await weekLogService.confirmWeek();
      navigate("/child/week");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <MobileShell showTabs={false}>
      <div
        style={{
          padding: "64px 24px 32px",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          background: "linear-gradient(165deg, var(--bg) 0%, #F4C9B8 120%)",
        }}
      >
        <Pill tone="butter">
          <Sparkles size={12} color="#E9C46A" fill="#E9C46A" /> Slutten av uka
        </Pill>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 38,
            fontWeight: 400,
            letterSpacing: -1,
            lineHeight: 1.05,
            marginTop: 14,
          }}
        >
          God jobba,
          <br />
          {profile.data.name}.
        </div>
        <div
          style={{
            fontSize: 15,
            color: "var(--ink-2)",
            marginTop: 12,
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          Du har logget{" "}
          <b>
            {logs.filter((l) => l.data.job_snapshot.kind !== "screen").length}{" "}
            jobber
          </b>
          . Bekreft før foreldrene runder av uka.
        </div>

        {screenJobStates
          .filter((s) => s.needsLogging)
          .map((s) => (
            <Card
              key={s.job.id}
              style={{ marginBottom: 12 }}
              data-testid="screen-time-card"
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--ink-3)",
                  marginBottom: 10,
                }}
              >
                {s.job.data.emoji} {s.job.data.title}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <input
                  type="text"
                  inputMode="decimal"
                  value={s.hours ?? ""}
                  data-testid="screen-time-input"
                  onChange={(e) => {
                    setScreenHoursMap((prev) => ({
                      ...prev,
                      [s.job.id]: e.target.value,
                    }));
                    setScreenErrorSet((prev) => ({
                      ...prev,
                      [s.job.id]: false,
                    }));
                  }}
                  style={{
                    width: 90,
                    height: 48,
                    padding: "0 12px",
                    border: `1px solid ${screenErrorSet[s.job.id] || (s.hours !== null && s.hours !== "" && !s.valid) ? "var(--bad)" : "var(--line-2)"}`,
                    borderRadius: 10,
                    fontSize: 20,
                    fontFamily: "var(--font-mono)",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                />
                <div style={{ fontSize: 14, color: "var(--ink-2)" }}>
                  timer
                  {s.maxHours !== null && (
                    <span style={{ color: "var(--ink-3)" }}>
                      {" "}
                      (maks {s.maxHours})
                    </span>
                  )}
                </div>
              </div>
              {s.band && (
                <div
                  className="num"
                  data-testid="screen-time-earned"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: s.band.amount >= 0 ? "var(--good)" : "var(--bad)",
                    marginTop: 10,
                  }}
                >
                  {s.band.amount > 0 ? "+" : ""}
                  {s.band.amount}{" "}
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--ink-2)",
                    }}
                  >
                    kr
                  </span>
                </div>
              )}
              {screenErrorSet[s.job.id] && (
                <div
                  data-testid="screen-time-error"
                  style={{ fontSize: 12, color: "var(--bad)", marginTop: 6 }}
                >
                  Fyll inn antall timer skjermtid
                </div>
              )}
            </Card>
          ))}

        <Card style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
          {summary.map(({ emoji, title, count, total }, i) => (
            <div
              key={title}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom:
                  i < summary.length - 1 ? "1px solid var(--line)" : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span>{emoji}</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
                <span
                  className="num"
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    background: "var(--bg-2)",
                    padding: "2px 7px",
                    borderRadius: 999,
                  }}
                >
                  ×{count}
                </span>
              </div>
              <span
                className="num"
                style={{
                  fontWeight: 700,
                  color: total >= 0 ? "var(--good)" : "var(--bad)",
                }}
              >
                {total >= 0 ? "+" : ""}
                {total} kr
              </span>
            </div>
          ))}
        </Card>

        <Card style={{ marginTop: "auto", marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "var(--ink-3)",
              }}
            >
              Du tjente
            </span>
            <Pill tone="good" size="sm">
              {logs.filter((l) => l.data.job_snapshot.kind !== "screen").length}{" "}
              jobber
            </Pill>
          </div>
          <div
            className="num"
            data-testid="week-total"
            style={{
              fontSize: 60,
              fontWeight: 700,
              letterSpacing: -2.5,
              lineHeight: 1,
              color: "var(--good)",
            }}
          >
            {displayTotal}{" "}
            <span
              style={{ fontSize: 22, color: "var(--ink)", fontWeight: 600 }}
            >
              kr
            </span>
          </div>
        </Card>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <Button
            variant="primary"
            size="lg"
            fullWidth
            data-testid="confirm-button"
            icon={<Check size={18} color="#fff" />}
            onClick={handleConfirm}
            disabled={confirming}
          >
            {confirming ? "Bekrefter…" : "Bekreft og send til foreldrene"}
          </Button>
          <Button
            variant="ghost"
            size="md"
            fullWidth
            onClick={() => navigate("/child/home")}
          >
            Jeg har glemt noe
          </Button>
        </div>
      </div>
    </MobileShell>
  );
}
